import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import React from 'react';

import { CustomDialogTypeEnum } from '@/components/dialog/model/CustomDialogTypeEnum.enum';
import { CustomDialogContentStyle } from '@/components/dialog/style/CustomDialog.style';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  cancelButtonColor?: ButtonProps['color'];
  cancelButtonText?: string;
  cancelButtonVariant?: ButtonProps['variant'];
  confirmButtonColor?: ButtonProps['color'];
  confirmButtonText?: string;
  confirmButtonVariant?: ButtonProps['variant'];
  dialogContent?: React.ReactNode;
  dialogTitle?: string;
  hideCancelButton?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => Promise<void> | void;
  type?: CustomDialogTypeEnum;
};

export const CustomDialog: React.FC<Props> = ({
  cancelButtonColor = 'secondary',
  cancelButtonText,
  cancelButtonVariant = 'outlined',
  confirmButtonColor = 'primary',
  confirmButtonText,
  confirmButtonVariant = 'contained',
  dialogContent,
  dialogTitle,
  hideCancelButton,
  isOpen,
  onClose: handleClose,
  onConfirm: handleConfirm,
  type,
}) => {
  const { t } = useTranslationWithPrefix('components.dialog.content');
  const returnDialogTitle = dialogTitle ?? t(`title.${type}`);
  const returnConfirmButtonColor = confirmButtonColor ?? type ?? 'primary';
  const returnCancelButtonColor = cancelButtonColor ?? type ?? 'secondary';
  const returnCancelButtonText = cancelButtonText ?? t('button.cancel');
  const returnConfirmButtonText = confirmButtonText ?? t('button.confirm');

  return (
    <Dialog disablePortal onClose={handleClose} open={isOpen}>
      <DialogTitle sx={{ py: 2 }}>{returnDialogTitle}</DialogTitle>
      {dialogContent && (
        <DialogContent sx={CustomDialogContentStyle}>
          {typeof dialogContent === 'string' ? (
            <DialogContentText>{dialogContent}</DialogContentText>
          ) : (
            dialogContent
          )}
        </DialogContent>
      )}
      <DialogActions sx={{ py: 2 }}>
        {!hideCancelButton && (
          <Button
            color={returnCancelButtonColor}
            onClick={handleClose}
            variant={cancelButtonVariant}
          >
            {returnCancelButtonText}
          </Button>
        )}
        <Button
          autoFocus
          color={returnConfirmButtonColor}
          onClick={handleConfirm}
          variant={confirmButtonVariant}
        >
          {returnConfirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
